import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useConversation } from '@elevenlabs/react';
import { GlassCard } from './reports/_shared/GlassCard';
import {
    Mic, MicOff, SendHorizontal, MessageCircle, Mail, Check,
    Bot, PhoneCall, PhoneIncoming, Send, Target, Play, Square,
} from 'lucide-react';

/*
 * Automations Portfolio (unbranded)
 * Route: /automations
 *
 * Deliberately generic per Suresh (2026-08-18): no company branding, no client
 * names, no specific metrics. The centerpiece is a chat-first live demo of the
 * outbound agent — text chat works without a mic, voice is opt-in, and a
 * scripted replay plays when the live agent is unreachable, so the demo UI
 * always does something.
 *
 * Session endpoints, tried in order:
 *   1. /api/voice/session                 (this Vercel project — needs env vars)
 *   2. audit bridge on our own infra      (always provisioned, CORS-locked)
 */

const SESSION_ENDPOINTS = [
    '/api/voice/session',
    'https://audit.socialseo.in/api/portfolio-voice/session',
];

// ───────────────────────────── content ─────────────────────────────

const STATS = [
    { value: 'Minutes', label: 'from a new lead landing to the first touch going out' },
    { value: '24/7', label: 'calls answered, DMs worked, replies classified' },
    { value: 'Zero', label: 'leads left sitting in an inbox waiting for a human' },
    { value: 'One CRM', label: 'every touch across every channel written back' },
];

const SYSTEMS = [
    {
        id: 'ai-setter',
        icon: Bot,
        name: 'AI DM Setter',
        lane: 'Outbound + Inbound',
        channel: 'Instagram DMs and comments',
        promise: 'Every comment and DM gets worked like a real setter would work it.',
        body: 'The setter reads unread DMs, comment replies and story replies, then moves each person through a staged qualifying conversation written in the founder\'s own voice. It knows the offer, the objections and the proof, so it can answer a real question instead of deflecting to a link. Qualified people get a booking link. Everyone else gets a straight answer and an exit. Every action lands in the CRM with the stage it moved to.',
        proof: [
            'Runs on platform-approved messaging permissions, not scraping hacks',
            'Staged qualifying script with per-thread pacing, daily caps and a kill switch',
            'Human typing cadence and a no-double-text guard, because account safety is the whole game',
            'Every conversation logged to the CRM with the stage it reached',
        ],
        stack: ['Official platform APIs', 'Browser automation', 'LLM brain', 'CRM sync'],
    },
    {
        id: 'outbound-voice',
        icon: PhoneCall,
        name: 'AI Outbound Calling',
        lane: 'Outbound',
        channel: 'Phone',
        promise: 'A caller that opens, handles the brush-off, and books the meeting.',
        body: 'The agent dials the list, opens with a reason for the call, and holds a real conversation through the first three brush-offs. It qualifies on the criteria you set, then books straight into the calendar. Calls that go nowhere are tagged with why, which is the part that makes next week\'s list better than this week\'s. You can talk to it yourself further up this page. That demo is the same engine, pointed at our own offer.',
        proof: [
            'Opens on a reason, not a pitch, so the first eight seconds survive',
            'Qualifies against your criteria before it ever asks for time',
            'Books into the calendar inside the call',
            'Every call transcribed, tagged with an outcome, and written back to the CRM',
        ],
        stack: ['Conversational voice AI', 'LLM brain', 'Calendar', 'CRM sync'],
    },
    {
        id: 'inbound-voice',
        icon: PhoneIncoming,
        name: 'AI Inbound Calling',
        lane: 'Inbound',
        channel: 'Phone',
        promise: 'Nobody reaches a voicemail at 9pm.',
        body: 'Inbound is where money leaks quietly. Someone calls, nobody picks up, and they call the next company on the list. The inbound agent answers on the first ring at any hour, works out what the caller needs, answers the questions it has been briefed on, and either books the call or routes a genuine emergency to a human. The caller gets a summary by message before they have put the phone down.',
        proof: [
            'Answers at any hour, so after-hours calls stop turning into lost leads',
            'Briefed on your offer, pricing bands and the questions you actually get asked',
            'Books or routes, then messages the caller a summary',
            'Same voice engine we run in production for long-form conversations',
        ],
        stack: ['Conversational voice AI', 'LLM brain', 'Calendar', 'WhatsApp'],
    },
    {
        id: 'whatsapp',
        icon: MessageCircle,
        name: 'WhatsApp Automation',
        lane: 'Outbound + Inbound',
        channel: 'WhatsApp',
        promise: 'New lead on your phone within minutes, with the first message already written.',
        body: 'Speed to lead decides who wins the deal, and most teams lose it to a form that emails somebody who checks email twice a day. Our gateway watches the lead table, and the moment a row lands it pings the owner on WhatsApp with the lead\'s details, a one-tap chat link and a drafted opener. The same gateway sends broadcasts, media and status updates from any system we run, with health checks that refuse to send when the session is not really connected.',
        proof: [
            'New leads reach the owner\'s phone within minutes of landing',
            'The watcher runs continuously under a lock, so nothing double-sends',
            'Drafted first message ships with the alert, so replying costs one tap',
            'Silent-hang watchdog, because a gateway that looks connected and sends nothing is the real failure mode',
        ],
        stack: ['WhatsApp gateway', 'Database triggers', 'Watchdogs'],
    },
    {
        id: 'cold-email',
        icon: Send,
        name: 'Cold Email Engine',
        lane: 'Outbound',
        channel: 'Email',
        promise: 'List built, sent, and every reply classified before you open the inbox.',
        body: 'We build the search, apply the suppression list so you never touch the same company twice, and hand off a verified list. Sending runs on its own infrastructure. The part teams skip is the reply side, and that is where we put the work: replies are pulled continuously, classified by intent, written into the CRM, and dropped into the right follow-up ladder. Interested people surface immediately. The rest keep moving without anyone remembering to move them.',
        proof: [
            'Suppression applied at search time, so you never touch the same company twice',
            'Verified lists only, so send budget never burns on bounces',
            'Every reply classified by intent within minutes of landing',
            'Follow-up ladders keep every thread moving without anyone remembering to move it',
        ],
        stack: ['List building', 'Verification', 'Sending infra', 'Reply classifier', 'CRM sync'],
    },
    {
        id: 'paid-funnel',
        icon: Target,
        name: 'Paid Inbound Funnel',
        lane: 'Inbound',
        channel: 'Paid ads to application',
        promise: 'A funnel measured on qualified conversations, not on clicks.',
        body: 'Landing page, application flow, pixel and server-side events, deduplicated leads, and stage tracking from first view to submitted application. The tracking matters more than the page. Most accounts we inherit are optimising on a raw pixel event shared across several sites, which means the numbers on the dashboard describe nothing. We scope every conversion to its own URL and match it to a row in a database you own, so a reported lead is a real person you can call.',
        proof: [
            'Measured on qualified conversations, never on cost per click',
            'Every conversion scoped to its own URL and matched to a row in a database you own',
            'Full stage tracking: page view, form start, per-step drop-off, submitted application',
            'Speed-to-lead alert fires the moment an application lands',
        ],
        stack: ['Landing pages', 'Pixel + server events', 'Database', 'Speed-to-lead alerts'],
    },
];

const RESOURCES = [
    {
        title: 'Production servers we own',
        body: 'Always-on infrastructure running the setters, gateways, watchers and renderers. Not a stack of no-code subscriptions that breaks when a trial ends.',
    },
    {
        title: 'Conversational voice stack',
        body: 'Production-grade voice AI with custom personas, tuned turn-taking and interruption handling. The agent on this page runs on it.',
    },
    {
        title: 'Browser automation fleet',
        body: 'Hardened browser rigs for platforms without APIs, with human-cadence guards, session persistence and kill switches.',
    },
    {
        title: 'Messaging gateways',
        body: 'WhatsApp and DM infrastructure with health checks, session watchdogs and fail-fast senders that stop on the first real error.',
    },
    {
        title: 'One data layer',
        body: 'Every system writes to one CRM and one database. Reporting is one query, not five exports stitched together at month end.',
    },
    {
        title: 'Silent-failure monitoring',
        body: 'Watchdogs that catch the expensive kind of down: the sender that looks alive and sends nothing.',
    },
];

const LESSONS = [
    {
        title: 'The optimisation goal is the audience brief',
        body: 'Ask a platform for landing page views and it finds the cheapest humans who will tap a link. Ask it for a completed application and it has to find people who finish. We watched one creative run against both goals on the same budget. The click-optimised version delivered thousands of impressions and not one application.',
    },
    {
        title: 'A lead you cannot match to a row is not a lead',
        body: 'A shared pixel across several properties once reported a healthy week of leads. The database behind the funnel had zero new rows. Count leads where they land, never where they are reported.',
    },
    {
        title: 'Reply handling beats send volume',
        body: 'Most outbound dies in the inbox, not in the send. Classifying replies the moment they land and routing them into follow-up ladders does more for booked calls than adding another thousand contacts.',
    },
    {
        title: 'A gateway that looks up and sends nothing is worse than one that is down',
        body: 'Silent failure is the expensive failure. Every sender we ship refuses to start when the session is not genuinely connected, and stops on the first real error instead of blasting the rest.',
    },
];

const ENGAGEMENT = [
    {
        step: '01',
        title: 'We map where the leads leak',
        body: 'One session on your current flow. Where leads land, who touches them, how long they wait, what gets counted. Most of the loss shows up in the gaps between tools, not inside them.',
    },
    {
        step: '02',
        title: 'We build the first system end to end',
        body: 'One channel, live and measured, before anything else starts. Usually whichever one is losing the most right now. You see real leads moving through it inside the first fortnight.',
    },
    {
        step: '03',
        title: 'We add channels and hand over the controls',
        body: 'Each new system plugs into the same CRM and the same reporting. You get the dashboards, the kill switches and the documentation, so nothing here depends on us staying.',
    },
];

const CALL_SCRIPT = [
    { speaker: 'agent', line: 'Hi, is this Ravi? Ravi, this is Arjun, calling from an automation studio. I know I have caught you cold, can I have thirty seconds to tell you why I called, and you can tell me to get lost?' },
    { speaker: 'user', line: 'Go on then.' },
    { speaker: 'agent', line: 'Appreciate it. We build the lead flow that sits behind agencies and B2B teams. The setter that works your Instagram DMs, the caller that dials your list, the WhatsApp ping that gets a new lead onto your phone within minutes. The reason I called you specifically is you are running paid traffic and hiring for a sales role at the same time, which usually means the leads are landing faster than anyone can work them.' },
    { speaker: 'user', line: 'We are managing. What does it cost?' },
    { speaker: 'agent', line: 'Depends on how many of those channels you want live, so let me ask two quick things instead of guessing. Right now, when a lead comes in through the ads, how long before a human actually speaks to them?' },
    { speaker: 'user', line: 'Honestly, could be the next day.' },
    { speaker: 'agent', line: 'That is the whole problem, and it is the cheapest one to fix. The gap between a lead landing and a human replying is where most of your ad spend goes. Before I take any more of your time, are you the person who would sign off on something like this, or is there someone else in that conversation?' },
    { speaker: 'user', line: 'It would be me and my co-founder.' },
    { speaker: 'agent', line: 'Perfect. Then here is what I would suggest. Twenty minutes with both of you, and I will map your current flow on the call and show you where the leads are dropping. If it is not worth fixing, I will tell you that and we are done. Does Thursday afternoon work, or is Friday morning easier?' },
];

// ───────────────────────────── primitives ─────────────────────────────

const Eyebrow = ({ children }) => (
    <p className="text-indigo-300/80 text-xs uppercase tracking-[0.25em] font-semibold mb-3">{children}</p>
);

const Bubble = ({ role, children, system }) => {
    if (system) {
        return (
            <div className="flex justify-center">
                <span className="max-w-md rounded-full bg-white/10 px-4 py-1.5 text-center text-xs text-white/60">{children}</span>
            </div>
        );
    }
    const isUser = role === 'user';
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed sm:max-w-[75%] ${
                    isUser
                        ? 'rounded-br-md bg-indigo-500/85 text-white'
                        : 'rounded-bl-md border border-white/10 bg-white/[0.07] text-white/90'
                }`}
            >
                {children}
            </div>
        </div>
    );
};

// ───────────────────────────── chat demo ─────────────────────────────

const ChatAgent = () => {
    // mode: null (idle) | 'text' | 'voice' | 'replay'
    const [mode, setMode] = useState(null);
    const [phase, setPhase] = useState('idle'); // idle | connecting | live | ended
    const [messages, setMessages] = useState([]); // {role, message} | {system: true, message}
    const [draft, setDraft] = useState('');
    const [replayIdx, setReplayIdx] = useState(0);
    const startedRef = useRef(false);
    const replayTimer = useRef(null);
    const logRef = useRef(null);
    const inputRef = useRef(null);
    const modeRef = useRef(null);
    modeRef.current = mode;

    const pushMsg = useCallback((m) => {
        setMessages((prev) => {
            const last = prev[prev.length - 1];
            // Drop the echo of a message we already appended optimistically
            if (last && !last.system && last.role === m.role && last.message === m.message) return prev;
            return [...prev, m];
        });
    }, []);

    const conversation = useConversation({
        onMessage: (m) => pushMsg({ role: m.role, message: m.message }),
        onDisconnect: () => {
            setPhase((p) => (p === 'live' ? 'ended' : p));
            if (modeRef.current !== 'replay') {
                pushMsg({ system: true, message: 'Call ended. Start again any time.' });
            }
            startedRef.current = false;
        },
        onError: (m) => console.error('Agent error:', m),
    });

    useEffect(() => {
        if (logRef.current) {
            logRef.current.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
        }
    }, [messages]);

    // Replay engine — plays the sample script as timed bubbles. Always works.
    useEffect(() => {
        if (mode !== 'replay') return undefined;
        if (replayIdx >= CALL_SCRIPT.length) return undefined;
        const turn = CALL_SCRIPT[replayIdx];
        const delay = replayIdx === 0 ? 500 : Math.min(600 + turn.line.length * 14, 3200);
        replayTimer.current = setTimeout(() => {
            pushMsg({ role: turn.speaker, message: turn.line });
            setReplayIdx((i) => i + 1);
        }, delay);
        return () => clearTimeout(replayTimer.current);
    }, [mode, replayIdx, pushMsg]);

    useEffect(() => {
        return () => {
            clearTimeout(replayTimer.current);
            conversation.endSession();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSignedUrl = useCallback(async () => {
        let lastErr = 'The live agent is unreachable right now.';
        for (const url of SESSION_ENDPOINTS) {
            try {
                const res = await fetch(url, { method: 'POST' });
                if (res.ok) {
                    const data = await res.json();
                    if (data.signedUrl) return data.signedUrl;
                } else {
                    const body = await res.json().catch(() => ({}));
                    if (body.error === 'rate_limited') {
                        throw Object.assign(new Error(body.message || 'Demo limit reached for this hour.'), { fatal: true });
                    }
                    // unprovisioned / upstream → try the next endpoint
                }
            } catch (err) {
                if (err.fatal) throw err;
                lastErr = 'The live agent is unreachable right now.';
            }
        }
        throw new Error(lastErr);
    }, []);

    const startReplay = useCallback(() => {
        clearTimeout(replayTimer.current);
        conversation.endSession();
        setMessages([{ system: true, message: 'Sample call replay. This is the script the live agent works from.' }]);
        setReplayIdx(0);
        setMode('replay');
        setPhase('live');
    }, [conversation]);

    const startLive = useCallback(
        async (wantVoice, firstText) => {
            if (startedRef.current) return;
            startedRef.current = true;
            clearTimeout(replayTimer.current);
            setMode(wantVoice ? 'voice' : 'text');
            setPhase('connecting');
            setMessages([]);

            if (wantVoice) {
                try {
                    await navigator.mediaDevices.getUserMedia({ audio: true });
                } catch {
                    setMessages([{ system: true, message: 'Microphone was blocked. You can still chat by typing, or allow the mic and try voice again.' }]);
                    setMode(null);
                    setPhase('idle');
                    startedRef.current = false;
                    return;
                }
            }

            try {
                const signedUrl = await getSignedUrl();
                await conversation.startSession({
                    signedUrl,
                    ...(wantVoice ? {} : { textOnly: true }),
                });
                setPhase('live');
                if (firstText) {
                    pushMsg({ role: 'user', message: firstText });
                    conversation.sendUserMessage(firstText);
                }
                if (!wantVoice) setTimeout(() => inputRef.current?.focus(), 150);
            } catch (err) {
                setMode(null);
                setPhase('idle');
                startedRef.current = false;
                setMessages([{ system: true, message: err.message }]);
                // Live agent unavailable → the UI still does something useful.
                if (!err.fatal) setTimeout(startReplay, 900);
            }
        },
        [conversation, getSignedUrl, pushMsg, startReplay]
    );

    const sendDraft = useCallback(() => {
        const t = draft.trim();
        if (!t) return;
        setDraft('');
        if (phase === 'live' && (mode === 'text' || mode === 'voice')) {
            pushMsg({ role: 'user', message: t });
            conversation.sendUserMessage(t);
            return;
        }
        // Typing from idle/replay/ended starts a fresh text session with this message.
        startedRef.current = false;
        startLive(false, t);
    }, [draft, phase, mode, conversation, pushMsg, startLive]);

    const endLive = useCallback(() => {
        if (mode === 'replay') {
            clearTimeout(replayTimer.current);
            setPhase('ended');
            pushMsg({ system: true, message: 'Replay stopped.' });
            return;
        }
        conversation.endSession();
    }, [mode, conversation, pushMsg]);

    const live = phase === 'live';
    const speaking = live && mode === 'voice' && conversation.isSpeaking;
    const statusText =
        phase === 'connecting' ? 'Connecting…'
        : mode === 'replay' && live ? 'Playing the sample call'
        : mode === 'voice' && live ? (speaking ? 'Agent speaking' : 'Listening, go ahead')
        : mode === 'text' && live ? 'Live, type as the prospect'
        : phase === 'ended' ? 'Session ended'
        : 'Ready when you are';

    const dotClass =
        phase === 'connecting' ? 'bg-amber-400'
        : live && mode !== 'replay' ? 'bg-emerald-400'
        : live ? 'bg-indigo-400'
        : 'bg-white/30';

    return (
        <GlassCard>
            {/* header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="relative flex h-2.5 w-2.5">
                        {live && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${dotClass}`} />}
                        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${dotClass}`} />
                    </span>
                    <div>
                        <p className="text-sm font-semibold text-white">Outbound agent, live demo</p>
                        <p className="text-xs text-white/50">{statusText}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {live || phase === 'connecting' ? (
                        <button
                            onClick={endLive}
                            className="flex items-center gap-1.5 rounded-lg border border-red-400/40 bg-red-500/15 px-3 py-1.5 text-xs font-semibold text-red-200 transition hover:bg-red-500/25"
                        >
                            <Square size={12} /> {mode === 'replay' ? 'Stop' : 'End'}
                        </button>
                    ) : (
                        <button
                            onClick={startReplay}
                            className="flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10"
                        >
                            <Play size={12} /> Watch the sample call
                        </button>
                    )}
                </div>
            </div>

            {/* transcript */}
            <div ref={logRef} className="mt-5 h-80 space-y-3 overflow-y-auto rounded-xl border border-white/10 bg-black/25 p-4">
                {messages.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                        <p className="text-sm text-white/60">You are the prospect. The agent is cold-calling you about lead-flow automation.</p>
                        <p className="text-xs text-white/40">Type a reply below to chat, or use the mic to talk to it. Brush it off, ask what it costs, tell it you are busy.</p>
                    </div>
                ) : (
                    messages.map((m, i) => (
                        <Bubble key={i} role={m.role} system={m.system}>{m.message}</Bubble>
                    ))
                )}
                {phase === 'connecting' && (
                    <div className="flex justify-start">
                        <span className="rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.07] px-4 py-2.5">
                            <span className="inline-flex gap-1">
                                {[0, 1, 2].map((i) => (
                                    <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/50" style={{ animationDelay: `${i * 140}ms` }} />
                                ))}
                            </span>
                        </span>
                    </div>
                )}
            </div>

            {/* input row */}
            <div className="mt-4 flex items-center gap-2">
                <input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') sendDraft(); }}
                    placeholder={live && mode === 'voice' ? 'You can also type while on the call…' : 'Type your reply as the prospect…'}
                    className="min-w-0 flex-1 rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder-white/35 outline-none transition focus:border-indigo-400/60"
                />
                <button
                    onClick={sendDraft}
                    disabled={!draft.trim()}
                    aria-label="Send"
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-indigo-500 text-white transition enabled:hover:bg-indigo-400 disabled:opacity-40"
                >
                    <SendHorizontal size={18} />
                </button>
                {mode === 'voice' && live ? (
                    <button
                        onClick={endLive}
                        aria-label="End voice call"
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-red-400/40 bg-red-500/15 text-red-200 transition hover:bg-red-500/25"
                    >
                        <MicOff size={18} />
                    </button>
                ) : (
                    <button
                        onClick={() => { startedRef.current = false; startLive(true); }}
                        disabled={phase === 'connecting'}
                        aria-label="Talk to the agent"
                        title="Talk to the agent"
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/20 text-white/80 transition hover:bg-white/10 disabled:opacity-40"
                    >
                        <Mic size={18} />
                    </button>
                )}
            </div>

            <p className="mt-4 text-xs text-white/40">
                Runs in your browser, no phone number needed. The demo is capped at a few live sessions per visitor per hour; the sample replay always plays.
            </p>
        </GlassCard>
    );
};

// ───────────────────────────── page ─────────────────────────────

const AutomationsPortfolio = () => {
    useEffect(() => {
        document.title = 'Automation Systems | Lead flow that runs without you';
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen">
            {/* hero */}
            <header className="container mx-auto max-w-6xl px-6 pt-20 pb-14 sm:pt-28">
                <Eyebrow>Outbound and inbound lead systems</Eyebrow>
                <h1 className="max-w-3xl text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-6xl">
                    Lead flow that runs without you<span className="text-indigo-400">.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">
                    We build the systems that catch a lead, qualify it, and put a booked call on
                    your calendar. Outbound and inbound, across DMs, WhatsApp, email and voice.
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                    <a href="#demo" className="rounded-xl bg-indigo-500 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:bg-indigo-400">
                        Talk to the agent
                    </a>
                    <a href="#systems" className="rounded-xl border border-white/25 px-6 py-3.5 text-center text-sm font-semibold text-white transition hover:bg-white/10">
                        See the systems
                    </a>
                </div>

                <dl className="mt-16 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-white/10 pt-10 lg:grid-cols-4">
                    {STATS.map((s) => (
                        <div key={s.label}>
                            <dt className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{s.value}</dt>
                            <dd className="mt-1.5 text-sm leading-snug text-white/50">{s.label}</dd>
                        </div>
                    ))}
                </dl>
            </header>

            {/* demo */}
            <section id="demo" className="container mx-auto max-w-6xl px-6 py-14">
                <div className="max-w-2xl">
                    <Eyebrow>Try it before you read about it</Eyebrow>
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">The demo is the portfolio piece</h2>
                    <p className="mt-4 text-lg leading-relaxed text-white/70">
                        Anyone can write that their AI agent sounds human. Chat with it or talk to
                        it and judge for yourself. We pointed it at our own offer, so you get to be
                        the prospect for once.
                    </p>
                </div>
                <div className="mt-10">
                    <ChatAgent />
                </div>
            </section>

            {/* systems */}
            <section id="systems" className="container mx-auto max-w-6xl px-6 py-14">
                <div className="max-w-2xl">
                    <Eyebrow>What we build</Eyebrow>
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Six systems, one pipeline</h2>
                    <p className="mt-4 text-lg leading-relaxed text-white/70">
                        Each one works alone. Together they cover the whole path from a stranger
                        seeing you to a qualified call on the calendar, with every touch written
                        back to one CRM.
                    </p>
                </div>

                <div className="mt-12 grid gap-6 lg:grid-cols-2">
                    {SYSTEMS.map((sys) => {
                        const Icon = sys.icon;
                        return (
                            <GlassCard key={sys.id} className="h-full">
                                <div className="flex items-center gap-3">
                                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-500/25 text-indigo-300">
                                        <Icon size={18} />
                                    </span>
                                    <div>
                                        <span className="block text-[11px] font-semibold uppercase tracking-wider text-indigo-300/90">{sys.lane}</span>
                                        <span className="block text-xs text-white/45">{sys.channel}</span>
                                    </div>
                                </div>

                                <h3 className="mt-4 text-xl font-bold tracking-tight text-white">{sys.name}</h3>
                                <p className="mt-2 text-[15px] font-medium leading-relaxed text-indigo-200">{sys.promise}</p>
                                <p className="mt-3 text-[15px] leading-relaxed text-white/65">{sys.body}</p>

                                <ul className="mt-5 space-y-2.5 border-t border-white/10 pt-5">
                                    {sys.proof.map((p) => (
                                        <li key={p} className="flex gap-2.5 text-sm leading-relaxed text-white/70">
                                            <Check className="mt-[3px] shrink-0 text-indigo-400" size={16} />
                                            <span>{p}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-6 flex flex-wrap gap-1.5">
                                    {sys.stack.map((t) => (
                                        <span key={t} className="rounded-md bg-white/10 px-2 py-1 font-mono text-[11px] text-white/60">{t}</span>
                                    ))}
                                </div>
                            </GlassCard>
                        );
                    })}
                </div>
            </section>

            {/* resources */}
            <section id="resources" className="container mx-auto max-w-6xl px-6 py-14">
                <div className="max-w-2xl">
                    <Eyebrow>What we run this on</Eyebrow>
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">The resources behind the systems</h2>
                    <p className="mt-4 text-lg leading-relaxed text-white/70">
                        These systems are not a pile of rented no-code subscriptions. They run on
                        infrastructure we own and operate.
                    </p>
                </div>
                <div className="mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
                    {RESOURCES.map((r) => (
                        <div key={r.title} className="border-t border-white/15 pt-6">
                            <h3 className="text-lg font-bold tracking-tight text-white">{r.title}</h3>
                            <p className="mt-2.5 text-[15px] leading-relaxed text-white/65">{r.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* lessons */}
            <section id="lessons" className="container mx-auto max-w-6xl px-6 py-14">
                <div className="max-w-2xl">
                    <Eyebrow>What running these actually taught us</Eyebrow>
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">The expensive lessons, free</h2>
                    <p className="mt-4 text-lg leading-relaxed text-white/70">
                        We paid for these with our own budget on our own funnels before any of it
                        touched anyone else's account.
                    </p>
                </div>
                <div className="mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
                    {LESSONS.map((l, i) => (
                        <div key={l.title} className="border-t border-white/15 pt-6">
                            <span className="font-mono text-xs text-indigo-300">{String(i + 1).padStart(2, '0')}</span>
                            <h3 className="mt-2 text-lg font-bold tracking-tight text-white">{l.title}</h3>
                            <p className="mt-2.5 text-[15px] leading-relaxed text-white/65">{l.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* engagement */}
            <section className="container mx-auto max-w-6xl px-6 py-14">
                <div className="max-w-2xl">
                    <Eyebrow>How an engagement runs</Eyebrow>
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        One channel live before we build the second
                    </h2>
                </div>
                <div className="mt-12 grid gap-8 md:grid-cols-3">
                    {ENGAGEMENT.map((e) => (
                        <div key={e.step}>
                            <span className="font-mono text-sm text-indigo-300">{e.step}</span>
                            <h3 className="mt-3 text-lg font-bold tracking-tight text-white">{e.title}</h3>
                            <p className="mt-2.5 text-[15px] leading-relaxed text-white/65">{e.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* contact */}
            <section id="contact" className="container mx-auto max-w-6xl px-6 pb-16 pt-6">
                <GlassCard>
                    <div className="py-4 sm:px-4">
                        <h2 className="max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl">
                            Tell us where your leads are going cold
                        </h2>
                        <p className="mt-4 max-w-xl text-lg leading-relaxed text-white/70">
                            Twenty minutes. We map your current flow on the call and point at the gap
                            that is costing the most. If there is nothing worth automating, we will
                            say so.
                        </p>
                        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                            <a href="#demo" className="flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-gray-900 transition hover:bg-white/90">
                                <MessageCircle size={16} /> Ask the agent anything
                            </a>
                            <a
                                href="mailto:hello@socialseo.in?subject=Lead-flow%20automation"
                                className="flex items-center justify-center gap-2 rounded-xl border border-white/25 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white/10"
                            >
                                <Mail size={16} /> Get in touch
                            </a>
                        </div>
                    </div>
                </GlassCard>
            </section>

            <footer className="border-t border-white/10 py-10 text-center">
                <p className="text-sm text-white/40">Every system on this page is one we run ourselves.</p>
            </footer>
        </div>
    );
};

export default AutomationsPortfolio;
